import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchAllUsers, exportUsersToExcel,updateUserAccessLevel, fetchAccessLevels,fetchNewUserAccessList, addNewUserAccess, deleteNewUserAccess } from '../../services/api';
import toast from 'react-hot-toast';
import {IoClose, IoSaveOutline, IoPersonAddOutline, IoTrashOutline, IoBusinessOutline } from 'react-icons/io5';
import {
    IoSettingsOutline, IoWalletOutline, IoPeopleOutline // Added new icon
} from 'react-icons/io5';

import Pagination from './Pagination';

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(new Date(dateString));
};

const ACCESS_LEVEL_COLORS = {
    basic: 'bg-gray-500/10 text-gray-400',
    free: 'bg-green-500/10 text-green-400',
    paid: 'bg-[#5652CF]/10 text-blue-400',
    premium: 'bg-purple-500/10 text-purple-400',
    default: 'bg-gray-500/10 text-gray-400',
};


// +++ NEW: MODAL COMPONENT FOR PRE-APPROVED USERS +++
const PreApprovedUserModal = ({ isOpen, onClose, userIdToken, accessLevelsConfig }) => {
    const [data, setData] = useState({ entries: [], total_pages: 1, current_page: 1 });
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    // Form state
    const [newEmail, setNewEmail] = useState('');
    const [newAccessLevel, setNewAccessLevel] = useState('basic');

    // NEW: State for the enterprise access toggle
    const [newEnterpriseAccess, setNewEnterpriseAccess] = useState(false);

    useEffect(() => {
        if (isOpen) {
            const fetchList = async (page = 1) => {
                setLoading(true);
                try {
                    const result = await fetchNewUserAccessList(userIdToken, { page });
                    setData(result);
                } catch (error) {
                    toast.error(`Failed to load list: ${error.toString()}`);
                } finally {
                    setLoading(false);
                }
            };
            fetchList(data.current_page);
        }
    }, [isOpen, userIdToken, refreshKey, data.current_page]);
    
    const handlePageChange = (newPage) => {
      if (newPage > 0 && newPage <= data.total_pages) {
          setData(prev => ({ ...prev, current_page: newPage }));
      }
    };

    const handleAddUser = async (e) => {
        e.preventDefault();
        if (!newEmail) {
            toast.error("Email is required.");
            return;
        }
        setIsSubmitting(true);
        const toastId = toast.loading("Adding user...");
        try {
            // NEW: Include enterprise_access in the payload
            const payload = { 
                email: newEmail, 
                access_level: newAccessLevel,
                enterprise_access: newEnterpriseAccess
            };
            await addNewUserAccess(userIdToken, payload);
            toast.success("User added to pre-approved list!", { id: toastId });
            setNewEmail(''); // Reset form
            setNewEnterpriseAccess(false); // Reset toggle
            setRefreshKey(prev => prev + 1); // Refresh list
        } catch (error) {
            toast.error(`Error: ${error.toString()}`, { id: toastId });
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleDelete = async (email) => {
        if (!window.confirm(`Are you sure you want to remove ${email} from the pre-approved list?`)) return;

        const toastId = toast.loading(`Deleting ${email}...`);
        try {
            await deleteNewUserAccess(userIdToken, email);
            toast.success("User removed successfully.", { id: toastId });
            setRefreshKey(prev => prev + 1); // Refresh list
        } catch (error) {
            toast.error(`Failed to delete: ${error.toString()}`, { id: toastId });
        }
    };


    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-1 rounded-xl shadow-2xl w-full max-w-2xl m-4 border border-line animate-fade-in-up flex flex-col" style={{maxHeight: '90vh'}}>
                <div className="flex items-center justify-between p-4 border-b border-line">
                    <h3 className="text-lg font-bold text-p-text flex items-center gap-2"><IoPeopleOutline /> Pre-Approved User Access</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-2 transition-colors">
                        <IoClose className="h-5 w-5 text-s-text" />
                    </button>
                </div>
                
                {/* Add User Form */}
                {/* MODIFIED: Add User Form */}
                <form onSubmit={handleAddUser} className="p-4 border-b border-line flex flex-col flex-row gap-3 items-end">
                    <div className="flex-grow w-full">
                        <label className="block mb-1 text-xs font-medium text-s-text">Email Address</label>
                        <input type="email" placeholder="new.user@example.com" value={newEmail} onChange={e => setNewEmail(e.target.value)} required className="w-full bg-2 border border-line rounded-lg px-3 py-2 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF]" />
                    </div>
                    <div className="w-full">
                         <label className="block mb-1 text-xs font-medium text-s-text">Access Level</label>
                        <select value={newAccessLevel} onChange={e => setNewAccessLevel(e.target.value)} className="w-full bg-2 border border-line rounded-lg px-3 py-2 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF]">
                           {accessLevelsConfig && Object.entries(accessLevelsConfig).map(([key, config]) => (
                               <option className={`${ACCESS_LEVEL_COLORS[key]}`} key={key} value={key}>{config.label}</option>
                           ))}
                        </select>
                    </div>
                    {/* NEW: Enterprise Access Toggle */}
                    <div className="w-full flex items-center h-[42px]">
                        <label className="flex items-center cursor-pointer select-none">
                            <input type="checkbox" checked={newEnterpriseAccess} onChange={e => setNewEnterpriseAccess(e.target.checked)} className="sr-only" />
                            <div className={`w-11 h-6 flex items-center rounded-full p-1 duration-300 ${newEnterpriseAccess ? 'bg-green-500' : 'bg-2'}`}>
                                <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${newEnterpriseAccess ? 'translate-x-5' : ''}`}></div>
                            </div>
                            <span className="ml-3 text-sm font-medium text-p-text">Enterprise</span>
                        </label>
                    </div>

                    <button type="submit" disabled={isSubmitting} className="w-full flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors">
                        <IoPersonAddOutline /> {isSubmitting ? 'Adding...' : 'Add User'}
                    </button>
                </form>


                {/* User List */}
                {/* MODIFIED: User List to show enterprise status */}
                <div className="flex-grow overflow-y-auto p-4">
                    {loading ? <p className="text-center text-s-text">Loading list...</p> : (
                        data.entries.length === 0 ? <p className="text-center text-s-text">No pre-approved users found.</p> : (
                            <div className="space-y-2">
                                {data.entries.map(entry => (
                                    <div key={entry.id} className="bg-2 p-3 rounded-lg flex flex-wrap md:flex-nowrap items-start md:items-center justify-between gap-4">
                                        <div>
                                            <p className="font-semibold text-p-text">{entry.email}</p>
                                            <p className="text-xs text-s-text">Access: <span className={`font-medium capitalize ${ACCESS_LEVEL_COLORS[entry.access_level]}`}>{entry.access_level}</span> | Added: {formatDate(entry.created_at)}</p>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            {/* NEW: Enterprise Status Badge */}
                                            {entry.enterprise_access && (
                                                 <span title="Enterprise Access Enabled" className="bg-[#5652CF]/10 text-blue-400 text-xs font-medium px-2 py-1 rounded-full flex items-center gap-1">
                                                    <IoBusinessOutline /> Enterprise
                                                 </span>
                                            )}
                                            {entry.signed_up ? (
                                                <span className="bg-green-500/10 text-green-400 text-xs font-medium px-2.5 py-0.5 rounded-full">Signed Up</span>
                                            ) : (
                                                <span className="bg-yellow-500/10 text-yellow-400 text-xs font-medium px-2.5 py-0.5 rounded-full">Pending</span>
                                            )}
                                             <button onClick={() => handleDelete(entry.email)} disabled={entry.signed_up} className="p-2 rounded-full text-red-500 hover:bg-red-500/10 disabled:text-gray-500 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors">
                                                <IoTrashOutline className="h-5 w-5"/>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>
                 {/* Pagination */}
                 {data.total_pages > 1 && (
                     <div className="p-4 border-t border-line flex items-center justify-center">
                        <Pagination currentPage={data.current_page} totalPages={data.total_pages} onPageChange={handlePageChange} totalUsers={data.total_entries} limit={10} />
                     </div>
                 )}
            </div>
        </div>
    );
};

export default PreApprovedUserModal;